import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Users, Shield, Mail, Plus, Trash2, UserPlus, Upload, FileSpreadsheet, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roles = [
  { value: "admin", label: "Admin", color: "bg-purple-100 text-purple-800", desc: "Tam yetki" },
  { value: "technical_manager", label: "Teknik Yönetici", color: "bg-blue-100 text-blue-800", desc: "Rapor onaylama" },
  { value: "planning_expert", label: "Planlama Uzmanı", color: "bg-green-100 text-green-800", desc: "Müşteri ve denetim planlaması" },
  { value: "auditor", label: "Denetçi", color: "bg-yellow-100 text-yellow-800", desc: "Denetim ve raporlama" },
  { value: "sales_representative", label: "Satış Temsilcisi", color: "bg-orange-100 text-orange-800", desc: "Müşteri satış takibi" },
  { value: "certificate_responsible", label: "Sertifika Sorumlusu", color: "bg-teal-100 text-teal-800", desc: "Sertifika yönetimi" },
  { value: "technical_reviewer", label: "Teknik İncelemeci", color: "bg-indigo-100 text-indigo-800", desc: "FR.48 teknik inceleme" },
  { value: "certification_committee", label: "Belgelendirme Komitesi", color: "bg-rose-100 text-rose-800", desc: "FR.48 sertifika kararı" }
];

const UserManagement = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [formEmail, setFormEmail] = useState("");
  const [formName, setFormName] = useState("");
  const [formRoles, setFormRoles] = useState([]);  // Changed to array for multiple roles
  const [submitting, setSubmitting] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);  // Track which user's roles are being edited
  const [editingRoles, setEditingRoles] = useState([]);  // Roles being edited

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      toast.error("Kullanıcılar yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (formRoles.length === 0) {
      toast.error("En az bir rol seçmelisiniz");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/users`, {
        email: formEmail,
        name: formName,
        role: formRoles[0],  // Primary role
        roles: formRoles  // All roles
      }, { withCredentials: true });
      toast.success("Kullanıcı başarıyla eklendi. Artık Google ile giriş yapabilir.");
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kullanıcı eklenemedi");
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle role in form
  const toggleFormRole = (roleValue) => {
    setFormRoles(prev => 
      prev.includes(roleValue) 
        ? prev.filter(r => r !== roleValue)
        : [...prev, roleValue]
    );
  };

  // Toggle role when editing existing user
  const toggleEditingRole = (roleValue) => {
    setEditingRoles(prev => 
      prev.includes(roleValue) 
        ? prev.filter(r => r !== roleValue)
        : [...prev, roleValue]
    );
  };

  // Start editing user roles
  const startEditingRoles = (user) => {
    setEditingUserId(user.user_id);
    setEditingRoles(user.roles || [user.role]);
  };

  // Save edited roles
  const saveEditingRoles = async () => {
    if (editingRoles.length === 0) {
      toast.error("En az bir rol seçmelisiniz");
      return;
    }
    try {
      await axios.put(`${API}/users/${editingUserId}`, { roles: editingRoles }, { withCredentials: true });
      toast.success("Roller güncellendi");
      setEditingUserId(null);
      setEditingRoles([]);
      fetchUsers();
    } catch (error) {
      toast.error("Roller güncellenemedi");
    }
  };

  // Cancel editing
  const cancelEditingRoles = () => {
    setEditingUserId(null);
    setEditingRoles([]);
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}`, { role: newRole, roles: [newRole] }, { withCredentials: true });
      toast.success("Rol güncellendi");
      fetchUsers();
    } catch (error) {
      toast.error("Rol güncellenemedi");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`${userName} kullanıcısını silmek istediğinizden emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success("Kullanıcı silindi");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kullanıcı silinemedi");
    }
  };

  const resetForm = () => {
    setFormEmail("");
    setFormName("");
    setFormRoles([]);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/export/users/template`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'kullanici_sablonu.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Şablon indirildi");
    } catch (error) {
      toast.error("Şablon indirilemedi");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx')) {
      toast.error("Sadece .xlsx dosyaları kabul edilir");
      return;
    }
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/import/users`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.error(err));
      }
      fetchUsers();
      setImportDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "İçe aktarma başarısız");
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const getRoleBadge = (role) => {
    const roleInfo = roles.find(r => r.value === role);
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${roleInfo?.color || "bg-gray-100 text-gray-800"}`}>
        {roleInfo?.label || role}
      </span>
    );
  };

  // Get all role badges for a user
  const getRoleBadges = (user) => {
    const userRoles = user.roles || [user.role];
    return (
      <div className="flex flex-wrap gap-1">
        {userRoles.map((role, idx) => (
          <span key={idx}>{getRoleBadge(role)}</span>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Kullanıcı Yönetimi</h1>
          <p className="text-[#4B5563]">Kullanıcıları ekleyin ve rollerini yönetin</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Import Dialog */}
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-users-btn">
                <Upload className="w-4 h-4 mr-2" />
                İçe Aktar
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Kullanıcı İçe Aktar</DialogTitle>
                <DialogDescription>Excel dosyasından kullanıcıları toplu olarak içe aktarın</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <Button variant="outline" onClick={handleDownloadTemplate} className="w-full gap-2">
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel Şablonunu İndir
                </Button>
                <div className="border-t pt-4">
                  <Label>Excel Dosyası Yükle (.xlsx)</Label>
                  <Input
                    type="file"
                    accept=".xlsx"
                    onChange={handleImport}
                    disabled={importing}
                    className="mt-2"
                    data-testid="import-users-file-input"
                  />
                  {importing && <p className="text-sm text-[#6B7280] mt-2">İçe aktarılıyor...</p>}
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Add User Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90" data-testid="add-user-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Kullanıcı Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Yeni Kullanıcı Ekle</DialogTitle>
                <DialogDescription>
                  Kullanıcıyı sisteme ekleyin. Eklenen kullanıcı Google ile giriş yapabilir.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                <div className="p-3 rounded-lg bg-[#E8F3EE] border border-[#1A4D2E]/20 text-sm">
                  <p className="text-[#4B5563]">
                    <strong>Not:</strong> Kullanıcının Google hesabına ait e-posta adresini girin. 
                    Kullanıcı bu e-posta ile Google ile giriş yapabilecek.
                  </p>
                </div>

                <div>
                  <Label htmlFor="name">Ad Soyad *</Label>
                  <Input
                    id="name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="Ahmet Yılmaz"
                    required
                    data-testid="user-name-input"
                  />
                </div>

                <div>
                <Label htmlFor="email">Google E-posta Adresi *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  placeholder="kullanici@gmail.com"
                  required
                  data-testid="user-email-input"
                />
              </div>

              <div>
                <Label>Roller * (birden fazla seçebilirsiniz)</Label>
                <div className="grid grid-cols-1 gap-2 mt-2 p-3 border rounded-lg bg-gray-50">
                  {roles.map((role) => (
                    <label 
                      key={role.value} 
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-white transition-colors ${formRoles.includes(role.value) ? 'bg-white shadow-sm border border-[#1A4D2E]' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={formRoles.includes(role.value)}
                        onChange={() => toggleFormRole(role.value)}
                        className="w-4 h-4 accent-[#1A4D2E]"
                      />
                      <div className="flex-1">
                        <span className={`text-sm font-medium ${formRoles.includes(role.value) ? 'text-[#1A4D2E]' : 'text-gray-700'}`}>
                          {role.label}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">- {role.desc}</span>
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${role.color}`}>
                        {formRoles.includes(role.value) ? '✓' : ''}
                      </span>
                    </label>
                  ))}
                </div>
                {formRoles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Seçili roller: {formRoles.map(r => roles.find(role => role.value === r)?.label).join(', ')}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  İptal
                </Button>
                <Button 
                  type="submit" 
                  className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90"
                  disabled={submitting || formRoles.length === 0}
                  data-testid="save-user-btn"
                >
                  {submitting ? "Ekleniyor..." : "Kullanıcı Ekle"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {roles.map((role) => (
          <Card key={role.value} className="border-[#E8F3EE]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#4B5563]">{role.label}</p>
                  <p className="text-2xl font-bold text-[#111827]">
                    {users.filter(u => u.role === role.value).length}
                  </p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${role.color.split(" ")[0]} flex items-center justify-center`}>
                  <Shield className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Users Table */}
      <Card className="border-[#E8F3EE]">
        <CardHeader>
          <CardTitle className="text-lg text-[#111827]">Tüm Kullanıcılar</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-[#4B5563]">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Henüz kullanıcı yok</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9FAFB]">
                      <TableHead>Kullanıcı</TableHead>
                      <TableHead>E-posta</TableHead>
                      <TableHead>Mevcut Rol</TableHead>
                      <TableHead>Rol Değiştir</TableHead>
                      <TableHead className="text-right">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.user_id} className="hover:bg-[#E8F3EE]/30">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="w-10 h-10">
                              <AvatarImage src={user.picture} />
                              <AvatarFallback className="bg-[#1A4D2E] text-white">
                                {user.name?.charAt(0) || "U"}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-[#111827]">{user.name}</p>
                              {user.user_id === currentUser?.user_id && (
                                <span className="text-xs text-[#9CA3AF]">(Siz)</span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-[#4B5563]">
                            <Mail className="w-4 h-4 text-[#9CA3AF]" />
                            {user.email}
                          </div>
                        </TableCell>
                        <TableCell>{getRoleBadges(user)}</TableCell>
                        <TableCell>
                          {editingUserId === user.user_id ? (
                            <div className="space-y-2">
                              <div className="flex flex-wrap gap-1">
                                {roles.map((role) => (
                                  <label 
                                    key={role.value}
                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs cursor-pointer transition-colors ${
                                      editingRoles.includes(role.value) 
                                        ? 'bg-[#1A4D2E] text-white' 
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={editingRoles.includes(role.value)}
                                      onChange={() => toggleEditingRole(role.value)}
                                      className="sr-only"
                                    />
                                    {role.label}
                                  </label>
                                ))}
                              </div>
                              <div className="flex gap-1">
                                <Button size="sm" onClick={saveEditingRoles} className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90 h-7 text-xs">
                                  Kaydet
                                </Button>
                                <Button size="sm" variant="outline" onClick={cancelEditingRoles} className="h-7 text-xs">
                                  İptal
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => startEditingRoles(user)}
                              disabled={user.user_id === currentUser?.user_id}
                              className="text-xs"
                            >
                              Rolleri Düzenle
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {user.user_id !== currentUser?.user_id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteUser(user.user_id, user.name)}
                              data-testid={`delete-user-${user.user_id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-[#E8F3EE]">
                {users.map((user) => (
                  <div key={user.user_id} className="p-4 hover:bg-[#E8F3EE]/30">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Avatar className="w-10 h-10 flex-shrink-0">
                          <AvatarImage src={user.picture} />
                          <AvatarFallback className="bg-[#1A4D2E] text-white">
                            {user.name?.charAt(0) || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="font-medium text-[#111827] truncate">
                            {user.name}
                            {user.user_id === currentUser?.user_id && (
                              <span className="text-xs text-[#9CA3AF] ml-1">(Siz)</span>
                            )}
                          </p>
                          <p className="text-xs text-[#4B5563] truncate">{user.email}</p>
                        </div>
                      </div>
                      {user.user_id !== currentUser?.user_id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteUser(user.user_id, user.name)}
                          className="h-8 w-8 flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <span className="text-sm text-[#4B5563]">Roller:</span>
                        {getRoleBadges(user)}
                      </div>
                      {user.user_id !== currentUser?.user_id && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => startEditingRoles(user)}
                          className="w-full text-xs mt-2"
                        >
                          Rolleri Düzenle
                        </Button>
                      )}
                      {editingUserId === user.user_id && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-2">
                          <div className="flex flex-wrap gap-1">
                            {roles.map((role) => (
                              <label 
                                key={role.value}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs cursor-pointer transition-colors ${
                                  editingRoles.includes(role.value) 
                                    ? 'bg-[#1A4D2E] text-white' 
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={editingRoles.includes(role.value)}
                                  onChange={() => toggleEditingRole(role.value)}
                                  className="sr-only"
                                />
                                {role.label}
                              </label>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={saveEditingRoles} className="flex-1 bg-[#1A4D2E] hover:bg-[#1A4D2E]/90 h-8 text-xs">
                              Kaydet
                            </Button>
                            <Button size="sm" variant="outline" onClick={cancelEditingRoles} className="flex-1 h-8 text-xs">
                              İptal
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManagement;
